import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import type { SalonRow } from '../../types/database.types';

/**
 * Política de Tratamiento de Datos y Términos y Condiciones del portal
 * público, generadas a partir de `claude/Plantillas_Legales_Salon.md`
 * (documento del proyecto, Parte I y Parte III) y enlazadas desde el
 * formulario de reserva (`Portal.tsx`, paso "datos") — ver
 * `supabase/schema_consentimiento_datos.sql`, que fue el fix que conectó
 * el consentimiento real al RPC de reserva.
 *
 * IMPORTANTE — esto sigue siendo una PLANTILLA, no texto legal
 * terminado: la nota de uso de `Plantillas_Legales_Salon.md` es
 * explícita en que un abogado (o la cámara de comercio) debe revisarlo
 * antes de considerarlo definitivo, sobre todo la política de datos. Por
 * eso ambas páginas muestran el aviso de revisión pendiente de forma
 * visible — quitarlo debe ser una decisión consciente del salón después
 * de esa revisión, no algo que quede oculto en el código.
 *
 * Los datos del salón (nombre, NIT, dirección, teléfono, correo) se leen
 * en vivo de la tabla `salones` — las mismas columnas que ya son públicas
 * para el portal (`salones_public_select` en schema.sql). Los campos que
 * NO se pueden derivar de la base de datos (ventana de cancelación,
 * política de inasistencias) se dejan marcados como pendientes, igual
 * que en la plantilla original — inventar un número aquí sería una
 * decisión de negocio que no le corresponde tomar a este código.
 */

function useSalonPublico() {
  const [salon, setSalon] = useState<SalonRow | null>(null);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    supabase
      .from('salones')
      .select('*')
      .eq('estado', 'activo')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setSalon(data);
        setCargando(false);
      });
  }, []);
  return { salon, cargando };
}

function Pendiente({ children }: { children: ReactNode }) {
  return <span className="rounded bg-gold-light/30 px-1.5 py-0.5 text-onyx/70">[{children}]</span>;
}

function LegalShell({
  titulo,
  children,
}: {
  titulo: string;
  children: (salon: SalonRow | null) => ReactNode;
}) {
  const { salon, cargando } = useSalonPublico();

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-onyx/10 bg-onyx px-4 py-6 text-center">
        <p className="font-display text-2xl tracking-wide text-gold-light">{salon?.nombre ?? 'Salón'}</p>
        <p className="mt-1 text-sm text-cream/70">{titulo}</p>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6 rounded-md border border-gold/40 bg-gold-light/10 p-3 text-xs text-onyx/70">
          Este texto es una <strong>plantilla de partida</strong> (ver <code>Plantillas_Legales_Salon.md</code>) —
          antes de considerarla definitiva debe revisarla un abogado o la cámara de comercio, en especial la
          Política de Tratamiento de Datos. Última actualización del texto: agosto de 2026.
        </div>

        {cargando ? (
          <p className="text-sm text-onyx/50">Cargando…</p>
        ) : (
          <article className="card space-y-4 text-sm leading-relaxed text-onyx/80">{children(salon)}</article>
        )}
      </main>
    </div>
  );
}

export function PoliticaDatos() {
  return (
    <LegalShell titulo="Política de Tratamiento de Datos Personales">
      {(salon: SalonRow | null) => (
        <>
          <h2 className="font-display text-lg text-onyx">1. Responsable del tratamiento</h2>
          <p>
            <strong>{salon?.nombre ?? <Pendiente>NOMBRE DEL SALÓN</Pendiente>}</strong>, identificado con NIT{' '}
            {salon?.nit ? <strong>{salon.nit}</strong> : <Pendiente>NIT</Pendiente>}, con domicilio en{' '}
            {salon?.direccion ? <strong>{salon.direccion}</strong> : <Pendiente>CIUDAD, DIRECCIÓN</Pendiente>}, correo
            de contacto {salon?.email ? <strong>{salon.email}</strong> : <Pendiente>CORREO</Pendiente>} y teléfono{' '}
            {salon?.telefono ? <strong>{salon.telefono}</strong> : <Pendiente>TELÉFONO</Pendiente>}, es responsable
            del tratamiento de los datos personales que recolecta a través de su portal de reservas y de su
            operación habitual, de conformidad con la Ley 1581 de 2012, el Decreto 1377 de 2013 y las demás normas
            que los desarrollen o modifiquen.
          </p>

          <h2 className="font-display text-lg text-onyx">2. Datos personales que se recolectan</h2>
          <p>De acuerdo con el módulo de clientas del sistema, se recolectan como mínimo:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Nombre y apellidos.</li>
            <li>Teléfono y WhatsApp.</li>
            <li>Correo electrónico.</li>
            <li>Fecha de nacimiento (dato opcional, suministrado voluntariamente).</li>
            <li>Historial de citas, servicios y compras asociado a la titular.</li>
          </ul>
          <p>
            No se recolectan datos sensibles (salud, origen étnico, orientación sexual, afiliación política o
            religiosa, entre otros) a través del portal de reservas.
          </p>

          <h2 className="font-display text-lg text-onyx">3. Finalidades del tratamiento</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Gestionar la agenda, confirmar, modificar o cancelar citas.</li>
            <li>Contactar a la clienta por teléfono, WhatsApp o correo para confirmaciones y recordatorios.</li>
            <li>Llevar el historial de servicios y compras de la clienta.</li>
            <li>Facturación y cumplimiento de obligaciones contables y tributarias.</li>
            <li>
              Enviar información comercial o promociones, <strong>únicamente si la titular lo autorizó</strong> para
              ese fin específico al reservar (casilla independiente y opcional).
            </li>
          </ul>

          <h2 className="font-display text-lg text-onyx">4. Derechos de las titulares</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Conocer, actualizar y rectificar sus datos personales.</li>
            <li>Solicitar prueba de la autorización otorgada.</li>
            <li>Ser informada sobre el uso que se ha dado a sus datos.</li>
            <li>Presentar quejas ante la Superintendencia de Industria y Comercio.</li>
            <li>Revocar la autorización o solicitar la supresión de sus datos, salvo deber legal de conservarlos.</li>
            <li>Acceder de forma gratuita a sus datos.</li>
          </ul>

          <h2 className="font-display text-lg text-onyx">5. Cómo ejercer estos derechos</h2>
          <p>
            Las solicitudes se pueden radicar al correo{' '}
            {salon?.email ? <strong>{salon.email}</strong> : <Pendiente>CORREO DE CONTACTO</Pendiente>} o al WhatsApp{' '}
            {salon?.telefono ? <strong>{salon.telefono}</strong> : <Pendiente>NÚMERO</Pendiente>}, indicando el
            nombre completo, el derecho que se desea ejercer y una descripción de la solicitud. Se dará respuesta
            dentro de los términos que establece la ley (10 días hábiles para consultas, 15 días hábiles para
            reclamos, prorrogables una vez informando el motivo del retraso).
          </p>

          <h2 className="font-display text-lg text-onyx">6. Medidas de seguridad</h2>
          <p>
            Los datos se almacenan en Supabase (PostgreSQL gestionado), con controles de acceso por usuario y rol
            (Row Level Security) que impiden que el personal del salón acceda a información fuera de lo que su rol
            autoriza, y que impiden que otra persona o negocio ajeno al salón pueda ver estos datos.
          </p>

          <h2 className="font-display text-lg text-onyx">7. Transferencia internacional de datos</h2>
          <p>
            Los datos se almacenan en la infraestructura de Supabase, que puede alojarse fuera de Colombia según la
            región configurada. Esto constituye una transmisión internacional de datos permitida bajo la Ley 1581
            cuando el encargado ofrece niveles adecuados de protección o la titular la autoriza, como ocurre al
            aceptar esta política.
          </p>

          <h2 className="font-display text-lg text-onyx">8. Vigencia</h2>
          <p>
            Esta política rige desde su publicación en el portal y permanecerá vigente mientras subsista la
            relación comercial y durante el tiempo que la ley exija conservar la información. Cualquier cambio
            sustancial se comunicará a través del portal.
          </p>
        </>
      )}
    </LegalShell>
  );
}

export function Terminos() {
  return (
    <LegalShell titulo="Términos y Condiciones del Portal de Reservas">
      {(salon: SalonRow | null) => {
        const nombreSalon = salon?.nombre ?? <Pendiente>NOMBRE DEL SALÓN</Pendiente>;
        return (
          <>
            <h2 className="font-display text-lg text-onyx">1. Objeto</h2>
            <p>
              Estos términos regulan el uso del portal de reservas en línea de <strong>{nombreSalon}</strong> (el
              "Portal"), a través del cual las clientas pueden consultar servicios, verificar disponibilidad y
              reservar citas.
            </p>

            <h2 className="font-display text-lg text-onyx">2. Aceptación</h2>
            <p>
              Al usar el Portal para reservar una cita, la usuaria acepta estos términos y la{' '}
              <a href="/portal/politica-datos" target="_blank" rel="noreferrer" className="text-gold-deep underline">
                Política de Tratamiento de Datos Personales
              </a>
              .
            </p>

            <h2 className="font-display text-lg text-onyx">3. Proceso de reserva</h2>
            <p>
              La reserva se confirma únicamente cuando el sistema entrega un número o mensaje de confirmación.
              Seleccionar un horario no garantiza la cita hasta completar la confirmación, ya que dos personas
              podrían intentar reservar el mismo espacio al mismo tiempo (el sistema lo resuelve automáticamente,
              asignando el horario a quien confirme primero).
            </p>

            <h2 className="font-display text-lg text-onyx">4. Cancelaciones y reprogramaciones</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Las citas se pueden cancelar o reprogramar hasta <Pendiente>X horas</Pendiente> antes de la hora
                agendada, sin costo — este número lo debe definir el salón.
              </li>
              <li>
                Cancelaciones fuera de ese plazo o inasistencias reiteradas sin aviso podrán dar lugar a{' '}
                <Pendiente>política de inasistencias por definir</Pendiente>.
              </li>
              <li>
                El salón podrá reprogramar o cancelar una cita por causas de fuerza mayor, avisando a la clienta por
                el medio de contacto registrado.
              </li>
            </ul>

            <h2 className="font-display text-lg text-onyx">5. Precios</h2>
            <p>
              Los precios publicados en el Portal corresponden a los vigentes al momento de la consulta y pueden
              cambiar sin previo aviso; el precio aplicable a una cita ya confirmada es el que estaba vigente al
              momento de confirmarla.
            </p>

            <h2 className="font-display text-lg text-onyx">6. Uso adecuado del Portal</h2>
            <p>
              La usuaria se compromete a suministrar datos veraces y a no usar el Portal para reservas falsas o
              abusivas. {nombreSalon} se reserva el derecho de restringir el acceso a quienes incumplan
              reiteradamente sus citas sin cancelar.
            </p>

            <h2 className="font-display text-lg text-onyx">7. Propiedad intelectual</h2>
            <p>
              Los contenidos del Portal (textos, imágenes, marca) son propiedad de {nombreSalon} o se usan con la
              debida autorización, y no pueden reproducirse sin permiso.
            </p>

            <h2 className="font-display text-lg text-onyx">8. Limitación de responsabilidad</h2>
            <p>
              {nombreSalon} no se hace responsable por fallas de disponibilidad del Portal originadas en proveedores
              externos de infraestructura, ni por resultados de servicios cuando la clienta no siguió las
              indicaciones o recomendaciones dadas por la profesional.
            </p>

            <h2 className="font-display text-lg text-onyx">9. Ley aplicable y jurisdicción</h2>
            <p>
              Estos términos se rigen por las leyes de la República de Colombia. Cualquier controversia se someterá
              a los jueces competentes del domicilio de {nombreSalon}, sin perjuicio de los mecanismos de protección
              al consumidor ante la Superintendencia de Industria y Comercio.
            </p>

            <h2 className="font-display text-lg text-onyx">10. Contacto</h2>
            <p>
              Para dudas sobre estos términos:{' '}
              {salon?.email ? <strong>{salon.email}</strong> : <Pendiente>CORREO</Pendiente>} /{' '}
              {salon?.telefono ? <strong>{salon.telefono}</strong> : <Pendiente>TELÉFONO / WHATSAPP</Pendiente>}.
            </p>

            <h2 className="font-display text-lg text-onyx">11. Modificaciones</h2>
            <p>
              Estos términos pueden actualizarse; la versión vigente siempre estará publicada en el Portal con su
              fecha de última actualización.
            </p>
          </>
        );
      }}
    </LegalShell>
  );
}
